// overlay.ts — the Capture screen's augmented overlay: marker outlines, the
// world axes at the reference marker, and the tracked SLAM map points, drawn
// on a 2D canvas laid over the object-fit:cover video. Projection uses the
// tracker's own pose and intrinsics (pinhole + Brown-Conrady distortion) in
// tracking-frame pixels, then maps those into the element's CSS pixels.

import type { Intrinsics, MapPointJSON, Pose } from '../api';
import type { FrameResultJSON } from './worker';

// projectWorld maps a world point to tracking-frame pixels via Xc = R·Xw + T.
export function projectWorld(pose: Pose, k: Intrinsics, X: readonly number[]): [number, number] | null {
  const R = pose.R, T = pose.T;
  const x = R[0] * X[0] + R[1] * X[1] + R[2] * X[2] + T[0];
  const y = R[3] * X[0] + R[4] * X[1] + R[5] * X[2] + T[1];
  const z = R[6] * X[0] + R[7] * X[1] + R[8] * X[2] + T[2];
  if (z <= 1e-6) return null;
  const xn = x / z, yn = y / z;
  const r2 = xn * xn + yn * yn;
  const radial = 1 + k.k1 * r2 + k.k2 * r2 * r2;
  const xd = xn * radial + 2 * k.p1 * xn * yn + k.p2 * (r2 + 2 * xn * xn);
  const yd = yn * radial + k.p1 * (r2 + 2 * yn * yn) + 2 * k.p2 * xn * yn;
  return [k.fx * xd + k.cx, k.fy * yd + k.cy];
}

export interface CoverMapping {
  scale: number; // element px per frame px
  ox: number;
  oy: number;
}

// coverMapping reproduces CSS object-fit: cover for a frame inside an element.
export function coverMapping(frameW: number, frameH: number, elW: number, elH: number): CoverMapping {
  const scale = Math.max(elW / frameW, elH / frameH);
  return { scale, ox: (elW - frameW * scale) / 2, oy: (elH - frameH * scale) / 2 };
}

const AXIS_LEN_M = 0.05;

export function drawOverlay(
  ctx: CanvasRenderingContext2D,
  elW: number,
  elH: number,
  frameW: number,
  frameH: number,
  result: FrameResultJSON | null,
  mapPoints: readonly MapPointJSON[],
  dpr: number,
): void {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, elW, elH);
  if (!result || !frameW || !frameH) return;
  const m = coverMapping(frameW, frameH, elW, elH);
  const toEl = (p: [number, number]): [number, number] => [p[0] * m.scale + m.ox, p[1] * m.scale + m.oy];
  // The tracker's intrinsics are in its own frame size; rescale if they differ.
  const k = result.intrinsics;
  const sx = k && k.width ? frameW / k.width : 1;
  const sy = k && k.height ? frameH / k.height : 1;
  const proj = (X: readonly number[]): [number, number] | null => {
    if (!k) return null;
    const p = projectWorld(result.pose, k, X);
    return p ? toEl([p[0] * sx, p[1] * sy]) : null;
  };

  if (result.have_pose && mapPoints.length) {
    for (const mp of mapPoints) {
      const p = proj(mp.pos);
      if (!p || p[0] < 0 || p[1] < 0 || p[0] > elW || p[1] > elH) continue;
      ctx.fillStyle = `rgb(${mp.color[0]},${mp.color[1]},${mp.color[2]})`;
      ctx.fillRect(p[0] - 2, p[1] - 2, 4, 4);
      ctx.strokeStyle = 'rgba(20, 184, 166, 0.9)';
      ctx.lineWidth = 1;
      ctx.strokeRect(p[0] - 2.5, p[1] - 2.5, 5, 5);
    }
  }

  for (const det of result.markers ?? []) {
    const pts = det.corners.map((c) => toEl([c[0], c[1]]));
    ctx.beginPath();
    pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1])));
    ctx.closePath();
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(20, 184, 166, 0.95)';
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.font = '600 13px "Fira Code", monospace';
    ctx.fillText(`#${det.payload.id} ${(det.payload.size_m * 1000).toFixed(0)}mm`, pts[0][0] + 4, pts[0][1] - 6);
  }

  if (result.have_pose) {
    const o = proj([0, 0, 0]);
    const axes: [number[], string][] = [
      [[AXIS_LEN_M, 0, 0], '#ef4444'],
      [[0, AXIS_LEN_M, 0], '#22c55e'],
      [[0, 0, AXIS_LEN_M], '#3b82f6'],
    ];
    if (o) {
      for (const [end, color] of axes) {
        const e = proj(end);
        if (!e) continue;
        ctx.beginPath();
        ctx.moveTo(o[0], o[1]);
        ctx.lineTo(e[0], e[1]);
        ctx.lineWidth = 4;
        ctx.strokeStyle = color;
        ctx.stroke();
      }
    }
  }
}
